import {
  property,
  state,
  html,
  customElement,
  css,
  cssManager,
  type TemplateResult,
  type CSSResult,
} from '@design.estate/dees-element';
import { DeesThumbnailBase } from '../dees-thumbnail-shared/DeesThumbnailBase.js';
import { thumbnailBaseStyles } from '../dees-thumbnail-shared/styles.js';
import { demo } from './demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-thumbnail-note': DeesThumbnailNote;
  }
}

@customElement('dees-thumbnail-note')
export class DeesThumbnailNote extends DeesThumbnailBase {
  public static demo = demo;
  public static demoGroups = ['Media'];
  public static styles = [
    ...thumbnailBaseStyles,
    css`
      .note-content {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        background: var(--dees-color-bg-primary);
        overflow: hidden;
      }

      .note-header {
        padding: 12px 14px 8px;
        flex-shrink: 0;
      }

      .note-title {
        font-size: 12px;
        font-weight: 600;
        color: var(--dees-color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.3;
      }

      .note-body {
        flex: 1;
        padding: 0 14px 14px;
        position: relative;
        overflow: hidden;
      }

      .note-text {
        font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
        font-size: 10px;
        line-height: 1.5;
        color: var(--dees-color-text-secondary);
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow: hidden;
        margin: 0;
      }

      .note-fade {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 60px;
        background: linear-gradient(
          transparent,
          var(--dees-color-bg-primary)
        );
        pointer-events: none;
      }

    `,
  ] as any;

  @property({ type: String })
  accessor title: string = '';

  @property({ type: String })
  accessor content: string = '';

  @property({ type: String })
  accessor language: string = '';

  @state()
  accessor isHovering: boolean = false;

  private noteBodyElement: HTMLElement | null = null;

  protected renderTileContent(): TemplateResult {
    const lines = this.content.split('\n');

    return html`
      <div class="note-content">
        ${this.title ? html`
          <div class="note-header">
            <div class="note-title">${this.title}</div>
          </div>
        ` : ''}

        <div class="note-body">
          <pre class="note-text">${lines.join('\n')}</pre>
          ${!this.isHovering ? html`<div class="note-fade"></div>` : ''}
        </div>

      </div>


    `;
  }

  protected renderBottomBar(): TemplateResult | string {
    const lines = this.content.split('\n');
    if (!this.label && !this.language && !lines.length) return '';
    return html`
      <div class="tile-info-bar">
        ${this.label ? html`<span class="info-label" title="${this.label}">${this.label}</span>` : ''}
        <span class="info-spacer"></span>
        ${this.language ? html`<span class="info-detail">${this.language.toUpperCase()}</span>` : ''}
        ${this.isHovering && lines.length > 12
          ? html`<span class="info-detail">Line ${this.getVisibleLineRange(lines.length)}</span>`
          : html`<span class="info-detail">${lines.length} lines</span>`}
      </div>
    `;
  }

  protected getTileClickDetail(): Record<string, unknown> {
    return {
      title: this.title,
      content: this.content,
      language: this.language,
    };
  }

  protected onTileMouseEnter(): void {
    this.isHovering = true;
    if (!this.noteBodyElement) {
      this.noteBodyElement = this.shadowRoot?.querySelector('.note-body') as HTMLElement;
    }
  }

  protected onTileMouseLeave(): void {
    this.isHovering = false;
    if (this.noteBodyElement) {
      this.noteBodyElement.scrollTop = 0;
    }
  }

  protected onTileMouseMove(e: MouseEvent): void {
    if (!this.isHovering || !this.noteBodyElement) return;

    const totalLines = this.content.split('\n').length;
    if (totalLines <= 12) return;

    const rect = this.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));

    const maxScroll = this.noteBodyElement.scrollHeight - this.noteBodyElement.clientHeight;
    this.noteBodyElement.scrollTop = percentage * maxScroll;
  }

  private getVisibleLineRange(totalLines: number): string {
    if (!this.noteBodyElement) return `1–12 of ${totalLines}`;
    const lineHeight = 15; // 10px font × 1.5 line-height
    const firstLine = Math.floor(this.noteBodyElement.scrollTop / lineHeight) + 1;
    const visibleCount = Math.floor(this.noteBodyElement.clientHeight / lineHeight);
    const lastLine = Math.min(firstLine + visibleCount - 1, totalLines);
    return `${firstLine}–${lastLine} of ${totalLines}`;
  }
}
